import { TruncateString } from 'foundations-components/transitional/components/TruncateString';
import { Text } from 'foundations-components/components/Text';
import styled from 'styled-components';
import { COMPANY_TYPE_ID, CONTACT_TYPE_ID } from 'customer-data-objects/constants/ObjectTypeIds';
import { SEARCH_QUERY } from '../components/ObjectSelectList';
import { CONTACT_PROPERTIES_FRAGMENT, COMPANY_PROPERTIES_FRAGMENT } from '../../../graphQL/fragments';
import { BlankIcon, CompaniesIcon, ContactsIcon } from 'foundations-assets';
import { IconCircle } from 'foundations-components/transitional/components/IconCircle';
import { View } from 'foundations-components/components/View';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
export function CardField({
  children
}) {
  return /*#__PURE__*/_jsx(TruncateString, {
    tooltip: children || undefined,
    placement: "bottom",
    children: children
  });
}
export function CardTitle({
  children
}) {
  return /*#__PURE__*/_jsx(Text, {
    as: "h6",
    typography: "heading-50",
    children: /*#__PURE__*/_jsx(TruncateString, {
      tooltip: children || undefined,
      placement: "bottom",
      marginBlockEnd: "0",
      children: children
    })
  });
}
export function isCompany(result) {
  return !!(result !== null && result !== void 0 && result.crmObjectsSearch.results.length && result.crmObjectsSearch.results[0].__typename === 'Company' && result.crmObjectsSearch.results[0].id);
}
export function isContact(result) {
  return !!(result !== null && result !== void 0 && result.crmObjectsSearch.results.length && result.crmObjectsSearch.results[0].__typename === 'Contact' && result.crmObjectsSearch.results[0].id);
}
export function readCompany(companyId, client) {
  return client.readFragment({
    fragment: COMPANY_PROPERTIES_FRAGMENT,
    fragmentName: 'CompanyProperties',
    id: `Company:${companyId}`
  });
}
export function readContact(contactId, client) {
  return client.readFragment({
    fragment: CONTACT_PROPERTIES_FRAGMENT,
    fragmentName: 'ContactProperties',
    id: `Contact:${contactId}`
  });
}
export function readSearch(objectTypeId, searchTerm, client) {
  return client.readQuery({
    query: SEARCH_QUERY,
    variables: {
      objectTypeId,
      searchTerm
    }
  });
}

// https://private.hubteamqa.com/canvas-storybook/?path=/docs/patterns-states-performance-and-loading--page#skeletons
export const LoadingSkeleton = styled.div.withConfig({
  displayName: "commonUtils__LoadingSkeleton",
  componentId: "sc-1xgbyuf-0"
})(({
  theme,
  height = '1rem',
  width = '12rem'
}) => {
  return {
    display: 'inline-block',
    borderRadius: theme.borderRadius[200],
    position: 'relative',
    overflow: 'hidden',
    backgroundColor: theme.color['fill-tertiary-default'],
    width,
    height,
    maxWidth: '100%',
    '&::after': {
      content: '""',
      position: 'absolute',
      top: 0,
      right: 0,
      bottom: 0,
      left: 0,
      transform: 'translateX(-100%)',
      backgroundImage: 'linear-gradient(90deg, rgba(204, 204, 204, 0) 0, rgba(204, 204, 204, 0.7) 50%, rgba(204, 204, 204, 0) 100%)',
      animation: 'shimmer 3s infinite linear',
      animationDelay: `${Math.random() / 2}s`
    },
    '@keyframes shimmer': {
      '100%': {
        transform: 'translateX(100%)'
      }
    }
  };
});
export function MultilineLoadingSkeleton({
  'data-test-id': dataTestId
}) {
  return /*#__PURE__*/_jsxs("div", {
    "data-test-id": dataTestId,
    style: {
      width: '100%',
      minWidth: '220px'
    },
    children: [/*#__PURE__*/_jsx(LoadingSkeleton, {
      width: "90%",
      children: "\xA0"
    }), /*#__PURE__*/_jsx("br", {}), /*#__PURE__*/_jsx(LoadingSkeleton, {
      width: "70%",
      children: "\xA0"
    }), /*#__PURE__*/_jsx("br", {}), /*#__PURE__*/_jsx(LoadingSkeleton, {
      width: "100%",
      children: "\xA0"
    }), /*#__PURE__*/_jsx("br", {})]
  });
}
function getPlaceholderAvatarIcon(objectTypeId) {
  switch (objectTypeId) {
    case CONTACT_TYPE_ID:
      return ContactsIcon;
    case COMPANY_TYPE_ID:
      return CompaniesIcon;
    default:
      return BlankIcon;
  }
}
export function PlaceholderAvatar({
  objectTypeId
}) {
  return /*#__PURE__*/_jsx(IconCircle, {
    icon: getPlaceholderAvatarIcon(objectTypeId),
    color: "icon-tertiary-default",
    backgroundColor: "fill-field-default",
    size: "900",
    padding: "300",
    borderWidth: "0"
  });
}
const LoadingOverlay = styled.div.withConfig({
  displayName: "commonUtils__LoadingOverlay",
  componentId: "sc-1xgbyuf-1"
})(["position:absolute;inset:0;overflow:hidden;border-radius:50%;&::after{content:'';position:absolute;top:0;right:0;bottom:0;left:0;transform:translateX(-100%);background-image:linear-gradient( 90deg,rgba(204,204,204,0) 0,rgba(204,204,204,0.7) 50%,rgba(204,204,204,0) 100% );animation:shimmer 3s infinite linear;}@keyframes shimmer{100%{transform:translateX(100%);}}"]);
export function PlaceholderAvatarLoading() {
  return /*#__PURE__*/_jsxs(View, {
    position: "relative",
    display: "inline-block",
    "data-test-id": "placeholder-avatar-loading",
    children: [/*#__PURE__*/_jsx(PlaceholderAvatar, {}), /*#__PURE__*/_jsx(LoadingOverlay, {
      "data-test-id": "loading-overlay"
    })]
  });
}

// it's worth noting that default associations will return the 'primary' association if one exists
export function getAssociatedCompany(contact) {
  var _contact$defaultAssoc, _contact$defaultAssoc2;
  return (_contact$defaultAssoc = contact === null || contact === void 0 || (_contact$defaultAssoc2 = contact.defaultAssociations.toCompanies) === null || _contact$defaultAssoc2 === void 0 || (_contact$defaultAssoc2 = _contact$defaultAssoc2.edges) === null || _contact$defaultAssoc2 === void 0 || (_contact$defaultAssoc2 = _contact$defaultAssoc2[0]) === null || _contact$defaultAssoc2 === void 0 ? void 0 : _contact$defaultAssoc2.node) !== null && _contact$defaultAssoc !== void 0 ? _contact$defaultAssoc : null;
}
export function getCompanyBillingContactAssociation(companyCRMObject) {
  var _companyCRMObject$crm;
  return (companyCRMObject === null || companyCRMObject === void 0 ? void 0 : companyCRMObject.crmObject.associationsBySpec) && (companyCRMObject === null || companyCRMObject === void 0 || (_companyCRMObject$crm = companyCRMObject.crmObject.associationsBySpec[0]) === null || _companyCRMObject$crm === void 0 || (_companyCRMObject$crm = _companyCRMObject$crm.associatedObjects) === null || _companyCRMObject$crm === void 0 || (_companyCRMObject$crm = _companyCRMObject$crm.edges[0]) === null || _companyCRMObject$crm === void 0 ? void 0 : _companyCRMObject$crm.node);
}